import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import { Thumbs } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { cn } from '@/lib/utils';

import 'swiper/css';
import 'swiper/css/thumbs';

export interface GalleryItem {
    url: string;
    type: 'image' | 'video';
    alt?: string;
    /** Pixel size, when known: with `adaptiveHeight` the box takes this ratio before the image loads */
    width?: number | null;
    height?: number | null;
}

interface ImageGalleryProps {
    images: GalleryItem[];
    height?: string;
    /**
     * Each image keeps its own ratio (never cropped) up to `maxHeight` instead of the fixed `height`;
     * the gallery follows the height of the image on screen.
     */
    adaptiveHeight?: boolean;
    maxHeight?: string;
    /** Tampilkan tombol prev/next di atas gambar utama. */
    showArrows?: boolean;
    showCounter?: boolean;
    showThumbnails?: boolean;
    className?: string;
}

const ratioStyle = (item: GalleryItem) =>
    item.width && item.height ? { aspectRatio: `${item.width} / ${item.height}` } : undefined;

const ImageGallery = ({
    images,
    height = 'h-[340px]',
    adaptiveHeight = false,
    maxHeight = 'max-h-[340px]',
    showArrows = true,
    showCounter = true,
    showThumbnails = true,
    className,
}: ImageGalleryProps) => {
    const [thumbSwiper, setThumbSwiper] = useState<SwiperType | null>(null);
    const [mainSwiper, setMainSwiper] = useState<SwiperType | null>(null);

    const [activeIndex, setActiveIndex] = useState(0);
    const [isBeginning, setIsBeginning] = useState(true);
    const [isEnd, setIsEnd] = useState(images.length <= 1);

    if (images.length === 0) return null;

    // Adaptive: full width at the image's own ratio, capped by maxHeight and letterboxed (object-contain)
    const sizeClassName = adaptiveHeight ? `h-auto ${maxHeight}` : height;

    return (
        <div className={cn('w-full', className)}>
            {/* Main Image */}
            <div className="relative overflow-hidden rounded-xl">
                <Swiper
                    modules={[Thumbs]}
                    autoHeight={adaptiveHeight}
                    thumbs={{
                        swiper: thumbSwiper && !thumbSwiper.destroyed ? thumbSwiper : null,
                    }}
                    onSwiper={(swiper) => {
                        setMainSwiper(swiper);
                        setIsBeginning(swiper.isBeginning);
                        setIsEnd(swiper.isEnd);
                    }}
                    onSlideChange={(swiper) => {
                        setActiveIndex(swiper.activeIndex);
                        setIsBeginning(swiper.isBeginning);
                        setIsEnd(swiper.isEnd);
                    }}
                >
                    {images.map((item, index) =>
                        item.type === 'video' ? (
                            <SwiperSlide key={`${item.url}-${index}`}>
                                {/* biome-ignore lint/a11y/useMediaCaption: no caption/transcript data exists for product media */}
                                <video
                                    src={item.url}
                                    controls
                                    style={adaptiveHeight ? ratioStyle(item) : undefined}
                                    className={`w-full object-contain ${sizeClassName}`}
                                    onLoadedMetadata={adaptiveHeight ? () => mainSwiper?.updateAutoHeight() : undefined}
                                />
                            </SwiperSlide>
                        ) : (
                            <SwiperSlide key={`${item.url}-${index}`}>
                                <img
                                    src={item.url}
                                    alt={item.alt ?? ''}
                                    style={adaptiveHeight ? ratioStyle(item) : undefined}
                                    className={`w-full object-contain ${sizeClassName}`}
                                    onLoad={adaptiveHeight ? () => mainSwiper?.updateAutoHeight() : undefined}
                                />
                            </SwiperSlide>
                        )
                    )}
                </Swiper>

                {/* Counter */}
                {showCounter && (
                    <div className="absolute bottom-3 left-3 z-20 rounded-full bg-black/60 px-3 py-1 text-white reg12">
                        {activeIndex + 1}/{images.length}
                    </div>
                )}

                {showArrows && images.length > 1 && (
                    <>
                        {/* Prev */}
                        <button
                            type="button"
                            aria-label="Gambar sebelumnya"
                            onClick={() => mainSwiper?.slidePrev()}
                            disabled={isBeginning}
                            className={cn(
                                'absolute left-4 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full transition',
                                isBeginning
                                    ? 'cursor-not-allowed bg-gray-300 text-gray-500'
                                    : 'bg-text-blue-100 text-white shadow'
                            )}
                        >
                            <ChevronLeft size={22} />
                        </button>

                        {/* Next */}
                        <button
                            type="button"
                            aria-label="Gambar berikutnya"
                            onClick={() => mainSwiper?.slideNext()}
                            disabled={isEnd}
                            className={cn(
                                'absolute right-4 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full transition',
                                isEnd
                                    ? 'cursor-not-allowed bg-gray-300 text-gray-500'
                                    : 'bg-text-blue-100 text-white shadow'
                            )}
                        >
                            <ChevronRight size={22} />
                        </button>
                    </>
                )}
            </div>

            {/* Thumbnail */}
            {showThumbnails && (
                <div className="mt-4 flex justify-center overflow-x-auto">
                    <Swiper
                        modules={[Thumbs]}
                        onSwiper={setThumbSwiper}
                        watchSlidesProgress
                        slidesPerView="auto"
                        spaceBetween={10}
                        className="!mx-0 !w-auto max-w-full"
                    >
                        {images.map((item, index) => {
                            const thumbClassName = cn(
                                'h-16 w-16 cursor-pointer  rounded-lg border-2 object-cover transition-all',
                                activeIndex === index
                                    ? 'border-text-blue-100 opacity-100'
                                    : 'border-transparent opacity-50'
                            );
                            return (
                                <SwiperSlide key={`${item.url}-${index}`} className="!w-[64px]">
                                    {item.type === 'video' ? (
                                        <video src={item.url} muted className={thumbClassName} />
                                    ) : (
                                        <img src={item.url} alt="" className={thumbClassName} />
                                    )}
                                </SwiperSlide>
                            );
                        })}
                    </Swiper>
                </div>
            )}
        </div>
    );
};

export default ImageGallery;
